import {
  useRouter,
  type NavigationGuardNext,
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { useRouterUtils } from '../useRouterUtils'
import { ROUTER_NAMES } from '@/constants/router/names'
import { signout } from '@/modules/miiaFw/utils/miiaFwCognito'
import { useStoreUtils } from '@/stores/util/useStoreUtils'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { FEATURE_OPTIONS } from '@/types/accessor/api/governmentSetting/governmentSetting'

export const loginGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  const storeUtils = useStoreUtils()
  storeUtils.allReset()
  signout()
  next()
}

/*
 * device登録画面のナビゲーションガードを定義
 */
export const deviceGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}

/*
 * Home画面のナビゲーションガードを定義
 */
export const homeGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}

/*
 * inspection_root画面のナビゲーションガードを定義
 */
export const inspectionRootGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}
/*
 * inspection_BasicInfo画面のナビゲーションガードを定義
 */
export const inspectionBasicInfoGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}
/*
 * inspection_BuildingInfo画面のナビゲーションガードを定義
 */
export const inspectionBuildingInfoGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}
/*
 * inspection_Step1画面のナビゲーションガードを定義
 */
export const inspectionStep1Guard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}

/*
 * inspection_Step2画面のナビゲーションガードを定義
 */
export const inspectionStep2Guard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}

/*
 * inspection_Result画面のナビゲーションガードを定義
 */
export const inspectionResultGuard = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
) => {
  await authGuardCore(to, next)
}

/**
 * 共通の認可ガード。指定パスの認可可否を判定し、未認可ならログインへリダイレクト＋サインアウト＋エラー通知。
 * @param to 遷移先ルート
 * @param next ルーターの`next`コールバック
 */
const authGuardCore = async (to: RouteLocationNormalizedGeneric, next: NavigationGuardNext) => {
  const router = useRouter()
  const { getAuthorizedEmergencyPaths, hasFeatureOption } = useRouterUtils(router)
  try {
    // トークンからユーザ属性を取得し、認証状態を確認
    const authorizedPaths = await getAuthorizedEmergencyPaths(to.path)
    if (authorizedPaths.length !== 0 && hasFeatureOption(FEATURE_OPTIONS.emergencyAuth)) {
      next()
      return
    }
    // 権限がないのでログイン画面（`/`）へ遷移
    next({ name: ROUTER_NAMES.login })
    // サインアウトし、エラーメッセージを表示する
    signout()
    throw new GlobalError([ERROR_CODES.LOGIN_AUTHZ_DENIED])
  } catch {
    next({ name: ROUTER_NAMES.login })
    signout()
    throw new GlobalError([ERROR_CODES.LOGIN_AUTHZ_DENIED])
  }
}
